import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { useAdminDarkMode } from "../../contexts/AdminDarkModeContext";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import ModeEditOutlineRoundedIcon from "@mui/icons-material/ModeEditOutlineRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import { FileModal } from "./DisplayFiles";
import "./files.css";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  IconButton,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  CircularProgress,
} from "@mui/material";
import { ZoomIn, ZoomOut, Close as CloseIcon } from "@mui/icons-material";
import { readFirstSheetAoA } from "../../utils/xlsxHelpers";
import { Row, Col } from "react-bootstrap";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Upload from "../../assets/Blue-sidebar-icon/upload.svg"
import WhiteButton from "../Button/WhiteButton";
import DragImage from "../../assets/Blue-sidebar-icon/Drag-image.svg"
import showToast from "../Toast/Toster";

// Allowed MIME types
const allowedTypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];

// Allowed file extensions (fallback validation)
const allowedExtensions = [".jpg", ".jpeg", ".png", ".pdf", ".xls", ".xlsx", ".txt", ".excel", ".csv"];

// File size limits in MB
const MAX_FILE_SIZE_MB = {
  image: 10,
  pdf: 25,
  excel: 20,
  txt: 5,
};

// Validation functions
const isValidFileType = (file, customAllowedTypes = allowedTypes, customAllowedExtensions = allowedExtensions) => {
  // Check MIME type first
  if (customAllowedTypes.includes(file.type)) {
    return true;
  }
  
  // Fallback: Check file extension
  const fileName = file.name || "";
  const fileExtension = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();
  return customAllowedExtensions.includes(fileExtension);
};

const getFileSizeLimitMB = (file) => {
  if (file.type?.startsWith("image/")) {
    return MAX_FILE_SIZE_MB.image;
  } else if (file.type === "application/pdf") {
    return MAX_FILE_SIZE_MB.pdf;
  } else if (
    file.type === "application/vnd.ms-excel" ||
    file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    file.name?.toLowerCase().endsWith(".xls") ||
    file.name?.toLowerCase().endsWith(".xlsx") ||
    file.name?.toLowerCase().endsWith(".excel")
  ) {
    return MAX_FILE_SIZE_MB.excel;
  } else if (file.type === "text/plain" || file.name?.toLowerCase().endsWith(".txt")) {
    return MAX_FILE_SIZE_MB.txt;
  }
  return MAX_FILE_SIZE_MB.pdf; // Default to PDF limit
};

const isValidFileSize = (file) => {
  const limitMB = getFileSizeLimitMB(file);
  const fileSizeMB = file.size / (1024 * 1024);
  return fileSizeMB <= limitMB;
};

function useMultiFileDropZoneSx() {
  const theme = useTheme();
  const { darkMode: adminDark } = useAdminDarkMode();
  const isShellDark = adminDark || theme.palette.mode === "dark";
  return useMemo(
    () => ({
      marginTop: "10px",
      boxSizing: "border-box",
      border: isShellDark ? "1px dashed #3d4f66" : "1px dashed #E5E7EB",
      borderRadius: "8px",
      backgroundColor: isShellDark ? "#151d2a" : "#F7F8FA",
      padding: "12px 14px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition:
        "background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
      "&:hover": {
        backgroundColor: isShellDark ? "#1a2332" : "#FFFFFF",
        border: isShellDark ? "1px dashed #60a5fa" : "1px dashed #2563EB",
        boxShadow: isShellDark
          ? "0 0 0 3px rgba(96, 165, 250, 0.18)"
          : "0 0 0 3px rgba(37, 99, 235, 0.1)",
      },
    }),
    [isShellDark]
  );
}

const Files = ({ files, setFiles, allowedFileTypes, allowedFileExtensions }) => {
  const multiDropSx = useMultiFileDropZoneSx();
  // Use custom allowed types if provided, otherwise use default
  const effectiveAllowedTypes = allowedFileTypes || allowedTypes;
  const effectiveAllowedExtensions = allowedFileExtensions || allowedExtensions;
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles = [];
      const invalidFiles = [];

      droppedFiles.forEach((file) => {
        if (!isValidFileType(file, effectiveAllowedTypes, effectiveAllowedExtensions)) {
          invalidFiles.push(file.name);
        } else if (!isValidFileSize(file)) {
          const limitMB = getFileSizeLimitMB(file);
          showToast.warning(`${file.name} exceeds the maximum size limit of ${limitMB}MB`);
        } else {
          validFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        showToast.warning("Some files are not allowed and were skipped.");
      }

      if (validFiles.length > 0) {
        setFiles([...files, ...validFiles]);
      }
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles = [];
      const invalidFiles = [];

      selectedFiles.forEach((file) => {
        if (!isValidFileType(file, effectiveAllowedTypes, effectiveAllowedExtensions)) {
          invalidFiles.push(file.name);
        } else if (!isValidFileSize(file)) {
          const limitMB = getFileSizeLimitMB(file);
          showToast.warning(`${file.name} exceeds the maximum size limit of ${limitMB}MB`);
        } else {
          validFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        showToast.warning("Some files are not allowed and were skipped.");
      }

      if (validFiles.length > 0) {
        setFiles([...files, ...validFiles]);
      }
    }
  };

  return (
    <Grid className="file-upload-container">
      <Grid
        className="file-upload file-upload--s7"
        sx={multiDropSx}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="Attachment"
          name="Attachment"
          className="file-input"
          multiple={true}
          accept={effectiveAllowedExtensions.join(",")}
          onChange={handleChange}
        />
        <label className="upload-files-label addQUoteFileSelect text-blue-color">
        <img src={DragImage} alt="" className="drag-image-file"/>&nbsp;
        Drag files here or click to select &nbsp;
          <span
            onClick={() => document.getElementById("Attachment").click()}
            className="select-file-link text-blue-color"
          >
            Select a File
          </span>
        </label>
      </Grid>
    </Grid>
  );
};

const File = ({
  file,
  setFile,
  index,
  allowedFileTypes,
  allowedFileExtensions,
  dropZoneClassName,
  /** Line-item style: soft solid border instead of dashed */
  dropZoneSolidBorder = false,
}) => {
  const theme = useTheme();
  const { darkMode: adminDark } = useAdminDarkMode();
  const isShellDark = adminDark || theme.palette.mode === "dark";
  // Use custom allowed types if provided, otherwise use default
  const effectiveAllowedTypes = allowedFileTypes || allowedTypes;
  const effectiveAllowedExtensions = allowedFileExtensions || allowedExtensions;
  const cdnUrl = import.meta.env.VITE_BASE_API;
  const [modalOpen, setModalOpen] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (!isValidFileType(droppedFile, effectiveAllowedTypes, effectiveAllowedExtensions)) {
        showToast.warning("File type not allowed");
      } else if (!isValidFileSize(droppedFile)) {
        const limitMB = getFileSizeLimitMB(droppedFile);
        showToast.warning(`${droppedFile.name} exceeds the maximum size limit of ${limitMB}MB`);
      } else {
        setFile(index, droppedFile);
      }
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (!isValidFileType(selectedFile, effectiveAllowedTypes, effectiveAllowedExtensions)) {
        showToast.warning("File type not allowed");
      } else if (!isValidFileSize(selectedFile)) {
        const limitMB = getFileSizeLimitMB(selectedFile);
        showToast.warning(`${selectedFile.name} exceeds the maximum size limit of ${limitMB}MB`);
      } else {
        setFile(index, selectedFile);
      }
    }
  };

  const handleRemove = () => {
    setFile(index, "");
    setFileInputKey(Date.now());
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const [previewFile, setPreviewFile] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [imageZoom, setImageZoom] = useState(100);
  const [previewContent, setPreviewContent] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(10);
  // Same-origin `blob:` URL for previewing PDFs by filename — the
  // cross-origin file URL was loading as a sandboxed PDF that could surface
  // as a broken file icon under Chrome's Site Isolation. The blob URL is
  // revoked on dialog close.
  const [previewPdfBlobUrl, setPreviewPdfBlobUrl] = useState(null);
  const imgRef = useRef(null);

  const updateImageSize = () => {
    if (imgRef.current && previewFile && previewModalOpen) {
      const img = imgRef.current;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      
      if (naturalWidth > 0 && naturalHeight > 0) {
        const containerWidth = img.parentElement?.clientWidth || 560;
        const padding = 40;
        const availableWidth = containerWidth - padding;
        
        if (imageZoom === 100) {
          // At 100%, fit to container (maintain aspect ratio)
          img.style.width = "auto";
          img.style.maxWidth = "100%";
          img.style.height = "auto";
          img.style.maxHeight = "none";
        } else {
          // When zoomed, use the displayed width at 100% as base
          // First, get what the width would be at 100%
          const displayWidthAt100 = Math.min(naturalWidth, availableWidth);
          // Then apply zoom to that base width
          const zoomedWidth = (displayWidthAt100 * imageZoom) / 100;
          img.style.width = `${zoomedWidth}px`;
          img.style.height = "auto";
          img.style.maxWidth = "none";
          img.style.maxHeight = "none";
        }
      }
    }
  };

  useEffect(() => {
    updateImageSize();
  }, [imageZoom, previewFile, previewModalOpen]);

  // CSV parsing function
  const parseCSV = (text) => {
    try {
      if (!text || typeof text !== "string") {
        throw new Error("Invalid text input for CSV parsing");
      }
      
      const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
      
      if (lines.length === 0) {
        return [];
      }
      
      return lines.map((line) => {
        const result = [];
        let current = "";
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          const nextChar = line[i + 1];
          
          if (char === '"') {
            if (inQuotes && nextChar === '"') {
              // Escaped quote
              current += '"';
              i++; // Skip next quote
            } else {
              // Toggle quote state
              inQuotes = !inQuotes;
            }
          } else if (char === "," && !inQuotes) {
            // Field separator
            result.push(current.trim());
            current = "";
          } else {
            current += char;
          }
        }
        // Add last field
        result.push(current.trim());
        return result;
      });
    } catch (error) {
      console.error("Error in parseCSV:", error);
      throw new Error(`CSV parsing failed: ${error.message}`);
    }
  };

  // Check if file is CSV or Excel
  const isCSVFile = (file) => {
    if (!file) return false;
    
    if (typeof file === "string") {
      const name = file.split("?")[0].toLowerCase(); // Remove query params
      return name.endsWith(".csv") || name.endsWith(".csvx");
    }
    if (file instanceof File || file instanceof Blob) {
      // Priority 1: Check by file name extension (most reliable)
      if (file.name) {
        const lowerName = file.name.toLowerCase();
        if (lowerName.endsWith(".csv") || lowerName.endsWith(".csvx")) {
          return true;
        }
      }
      // Priority 2: Check by MIME type
      if (file.type) {
        const lowerType = file.type.toLowerCase();
        if (lowerType === "text/csv" || lowerType === "application/csv" || lowerType.includes("csv")) {
          return true;
        }
      }
    }
    return false;
  };

  const isExcelFile = (file) => {
    if (!file) return false;
    
    if (typeof file === "string") {
      // Check file extension for string files (from server)
      const lowerFile = file.toLowerCase();
      return lowerFile.endsWith(".xls") || lowerFile.endsWith(".xlsx") || lowerFile.endsWith(".excel");
    }
    if (file instanceof File || file instanceof Blob) {
      // Priority 1: Check by file name extension (most reliable)
      if (file.name) {
        const lowerName = file.name.toLowerCase();
        if (lowerName.endsWith(".xls") || lowerName.endsWith(".xlsx") || lowerName.endsWith(".excel")) {
          return true;
        }
      }
      // Priority 2: Check by MIME type (less reliable, browser dependent)
      if (file.type) {
        const lowerType = file.type.toLowerCase();
        if (lowerType.includes("excel") || 
            lowerType === "application/vnd.ms-excel" ||
            lowerType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            lowerType === "application/vnd.ms-excel.sheet.macroenabled.12") {
          return true;
        }
      }
    }
    return false;
  };

  const openPreviewModal = async () => {
    if (!file) {
      return;
    }

    // Open modal immediately
    setPreviewFile(file);
    setPreviewModalOpen(true);
    setImageZoom(100);
    setPreviewPage(0);
    setPreviewRowsPerPage(10);
    setPreviewContent(null);
    setPreviewPdfBlobUrl(null);
    setIsLoadingPreview(false);

    // For string PDF filenames, fetch and use a same-origin `blob:` URL —
    // the cross-origin file URL was loading as a sandboxed PDF that could
    // surface as a broken file icon under Chrome's Site Isolation.
    if (typeof file === "string" && file.toLowerCase().endsWith(".pdf")) {
      try {
        const fileUrl = `${cdnUrl}/v1/cdn/upload/${file}`;
        const response = await fetch(fileUrl);
        if (response.ok) {
          const rawBlob = await response.blob();
          // Force application/pdf so the iframe renders the PDF viewer
          // instead of triggering a download.
          const pdfBlob =
            rawBlob.type === "application/pdf"
              ? rawBlob
              : new Blob([rawBlob], { type: "application/pdf" });
          setPreviewPdfBlobUrl(URL.createObjectURL(pdfBlob));
        }
      } catch (err) {
        console.error("PDF preview fetch failed:", err);
      }
    }

    // Parse CSV or Excel files
    const isCSV = isCSVFile(file);
    const isExcel = isExcelFile(file);
    
    if (isCSV || isExcel) {
      setIsLoadingPreview(true);
      try {
        let blob = null;
        
        if (file instanceof File || file instanceof Blob) {
          blob = file;
        } else if (typeof file === "string") {
          const fileUrl = `${cdnUrl}/v1/cdn/upload/${file}`;
          const response = await fetch(fileUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.status}`);
          }
          blob = await response.blob();
        }

        if (!blob) {
          throw new Error("Could not get file blob");
        }

        if (isCSV) {
          const text = await blob.text();
          if (!text || text.trim().length === 0) {
            throw new Error("CSV file is empty");
          }
          const rows = parseCSV(text);
          if (rows.length > 0) {
            setPreviewContent({ type: "csv", data: rows });
          } else {
            throw new Error("CSV file has no valid rows");
          }
        } else if (isExcel) {
          const arrayBuffer = await blob.arrayBuffer();
          if (!arrayBuffer || arrayBuffer.byteLength === 0) {
            throw new Error("Excel file is empty or invalid");
          }
          const data = await readFirstSheetAoA(arrayBuffer);
          if (!data || data.length === 0) {
            throw new Error("Excel file is empty or has no sheets");
          }
          setPreviewContent({ type: "excel", data: data });
        }
      } catch (error) {
        setPreviewContent({ type: isCSV ? "csv" : "excel", error: error.message || "Unknown error", data: null });
        showToast.error(`Failed to preview ${isCSV ? "CSV" : "Excel"} file: ${error.message || "Unknown error"}`);
      } finally {
        setIsLoadingPreview(false);
      }
    }
  };

  const closePreviewModal = () => {
    if (previewPdfBlobUrl && previewPdfBlobUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewPdfBlobUrl);
    }
    setPreviewPdfBlobUrl(null);
    setPreviewModalOpen(false);
    setPreviewFile(null);
    setPreviewContent(null);
    setImageZoom(100); // Reset zoom when closing
    setPreviewPage(0);
    setPreviewRowsPerPage(10);
  };

  const handlePreviewPageChange = (event, newPage) => {
    setPreviewPage(newPage);
  };

  const handlePreviewRowsPerPageChange = (event) => {
    setPreviewRowsPerPage(parseInt(event.target.value, 10));
    setPreviewPage(0);
  };

  const getPreviewData = () => {
    if (!previewContent?.data) return [];
    const dataWithoutHeader = previewContent.data.slice(1);
    const startIndex = previewPage * previewRowsPerPage;
    return dataWithoutHeader.slice(startIndex, startIndex + previewRowsPerPage);
  };

  const getTotalRows = () => {
    if (!previewContent?.data?.length) return 0;
    return Math.max(0, previewContent.data.length - 1);
  };

  const handleZoomIn = () => {
    setImageZoom((prev) => Math.min(prev + 25, 300)); // Max 300%
  };

  const handleZoomOut = () => {
    setImageZoom((prev) => Math.max(prev - 25, 50)); // Min 50%
  };

  const handleResetZoom = () => {
    setImageZoom(100);
  };

  useEffect(() => {
    return () => {
      if (file instanceof Blob) {
        URL.revokeObjectURL(file.preview);
      }
    };
  }, [file]);

  /* Line-item: same band height as Description (100px); §7 dashed drop zone */
  const LINE_ITEM_ATTACH_H = 100;
  const lineItemSolid = Boolean(dropZoneSolidBorder);
  const hasAttachment =
    !!file &&
    (typeof file !== "string" || String(file).trim() !== "");

  const lineItemDropSx = useMemo(() => {
    if (!lineItemSolid) return null;
    const D = isShellDark;
    return {
      boxSizing: "border-box",
      border: D ? "1px dashed #3d4f66" : "1px dashed #E5E7EB",
      backgroundColor: D ? "#151d2a" : "#F7F8FA",
      borderRadius: "8px",
      cursor: !hasAttachment ? "pointer" : "default",
      padding: "0 12px",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition:
        "background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
      ...(!hasAttachment && {
        "&:hover": {
          backgroundColor: D ? "#1a2332" : "#FFFFFF",
          border: D ? "1px dashed #60a5fa" : "1px dashed #2563EB",
          boxShadow: D
            ? "0 0 0 3px rgba(96, 165, 250, 0.18)"
            : "0 0 0 3px rgba(37, 99, 235, 0.1)",
        },
      }),
      ...(hasAttachment
        ? { minHeight: `${LINE_ITEM_ATTACH_H}px`, height: "auto" }
        : {
            height: `${LINE_ITEM_ATTACH_H}px`,
            minHeight: `${LINE_ITEM_ATTACH_H}px`,
            maxHeight: `${LINE_ITEM_ATTACH_H}px`,
          }),
    };
  }, [lineItemSolid, hasAttachment, isShellDark]);

  const dropZoneStyle = lineItemSolid
    ? undefined
    : {
        maxHeight: "70px",
        minHeight: "55px",
        height: "100%",
        border: "1px dashed",
        cursor: !hasAttachment ? "pointer" : "default",
      };

  return (
    <Grid style={lineItemSolid ? { padding: 0, margin: 0, minHeight: 0 } : undefined}>
      <Grid
        className={[
          "file-upload fileUpload",
          lineItemSolid ? "" : "px-3 py-2",
          dropZoneClassName || "border-blue-color",
          hasAttachment ? "has-file" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        sx={lineItemSolid ? lineItemDropSx : undefined}
        style={dropZoneStyle}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={() =>
          !hasAttachment &&
          document.getElementById(`single-attachment-${index}`).click()
        }
      >
        <input
          key={fileInputKey}
          ref={fileInputRef}
          type="file"
          id={`single-attachment-${index}`}
          name="single-attachment"
          accept={effectiveAllowedExtensions.join(",")}
          style={{ display: "none" }}
          onChange={handleChange}
          multiple={false}
        />
        <span
          className="d-flex flex-column w-100 justify-content-center align-items-center"
          style={{
            minHeight: lineItemSolid ? "100%" : "120px",
            height: lineItemSolid ? "100%" : undefined,
            lineHeight: lineItemSolid ? 0 : undefined,
          }}
        >
          {!hasAttachment ? (
            <img
              src={Upload}
              alt=""
              className={lineItemSolid ? "cs-line-item-upload-icon" : undefined}
              style={
                lineItemSolid
                  ? { display: "block", maxHeight: 32, width: "auto", margin: 0 }
                  : undefined
              }
            />
          ) : (
            <Row className="w-100 p-0 d-flex ">
              <Col
                className="col-8 p-0 d-flex"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (file) {
                    openPreviewModal();
                  }
                }}
                style={{ cursor: "pointer" }}
                xl={8}
              >
                {file ? (
                  typeof file === "string" ? (
                    file.toLowerCase().endsWith(".pdf") ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <PictureAsPdfIcon
                          style={{
                            width: "40px",
                            height: "40px",
                            marginRight: "10px",
                            color: "#d32f2f",
                          }}
                        />
                        <span>{file}</span>
                      </div>
                    ) : file.toLowerCase().match(/\.(xls|xlsx|excel)$/i) ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <DescriptionIcon
                          style={{
                            width: "40px",
                            height: "40px",
                            marginRight: "10px",
                            color: "#217346",
                          }}
                        />
                        <span>{file}</span>
                      </div>
                    ) : file.toLowerCase().endsWith(".csv") || file.toLowerCase().endsWith(".csvx") ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <DescriptionIcon
                          style={{
                            width: "40px",
                            height: "40px",
                            marginRight: "10px",
                            color: "#0f2b46",
                          }}
                        />
                        <span>{file}</span>
                      </div>
                    ) : file.toLowerCase().endsWith(".txt") ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <InsertDriveFileIcon
                          style={{
                            width: "40px",
                            height: "40px",
                            marginRight: "10px",
                            color: "#0f2b46",
                          }}
                        />
                        <span>{file}</span>
                      </div>
                    ) : (
                      <div>
                        <img
                          src={`${cdnUrl}/v1/cdn/upload/${file}`}
                          height="50px"
                          alt={file}
                          style={{ width: "100%", marginTop: "35px", }}
                        />
                      </div>
                    )
                  ) : file instanceof Blob || file instanceof File ? (
                    file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf") ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <PictureAsPdfIcon
                          style={{
                            width: "40px",
                            height: "40px",
                            marginRight: "10px",
                            color: "#d32f2f",
                          }}
                        />
                        <span>{file?.name || "Uploaded File"}</span>
                      </div>
                    ) : file.type === "application/vnd.ms-excel" ||
                      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                      file.name?.toLowerCase().match(/\.(xls|xlsx|excel)$/i) ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <DescriptionIcon
                          style={{
                            width: "40px",
                            height: "40px",
                            marginRight: "10px",
                            color: "#217346",
                          }}
                        />
                        <span>{file?.name || "Uploaded File"}</span>
                      </div>
                    ) : file.type === "text/csv" || file.type === "application/csv" || file.name?.toLowerCase().endsWith(".csv") || file.name?.toLowerCase().endsWith(".csvx") ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <DescriptionIcon
                          style={{
                            width: "40px",
                            height: "40px",
                            marginRight: "10px",
                            color: "#0f2b46",
                          }}
                        />
                        <span>{file?.name || "Uploaded File"}</span>
                      </div>
                    ) : file.type === "text/plain" || file.name?.toLowerCase().endsWith(".txt") ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <InsertDriveFileIcon
                          style={{
                            width: "40px",
                            height: "40px",
                            marginRight: "10px",
                            color: "#0f2b46",
                          }}
                        />
                        <span>{file?.name || "Uploaded File"}</span>
                      </div>
                    ) : (
                      <div
                        style={{ height: "50px" }}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file?.name || "Uploaded File"}
                          style={{ height: "100%", marginTop: "35px", marginLeft: "-8px" }}
                          onLoad={(e) => URL.revokeObjectURL(e.target.src)} 
                        />
                      </div>
                    )
                  ) : (
                    ""
                  )
                ) : (
                  <span>No file uploaded</span>
                )}
              </Col>

              <Dialog
                open={previewModalOpen}
                onClose={closePreviewModal}
                maxWidth={previewContent?.type === "csv" || previewContent?.type === "excel" ? "lg" : "sm"}
                PaperProps={{
                  style: {
                    maxHeight: previewContent?.type === "csv" || previewContent?.type === "excel" ? "90vh" : "75vh",
                    height: "auto",
                    width: "90%",
                    maxWidth: previewContent?.type === "csv" || previewContent?.type === "excel" ? "1200px" : "600px",
                    display: "flex",
                    flexDirection: "column",
                  },
                }}
              >
                <DialogTitle
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: "#fff",
                  }}
                >
                  <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Typography 
                      className="text-blue-color borerBommoModel"
                      style={{ fontWeight: 600, fontSize: "18px" }}
                    >
                      Preview
                    </Typography>
                    {(previewContent?.type === "csv" || previewContent?.type === "excel") && (
                      <Chip
                        label={previewContent?.type === "csv" ? "CSV" : "EXCEL"}
                        style={{
                          backgroundColor: previewContent?.type === "csv" ? "#0f2b46" : "#217346",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "12px",
                          height: "24px",
                        }}
                      />
                    )}
                  </Box>
                  {previewFile && 
                   !(typeof previewFile === "string" && previewFile.toLowerCase().endsWith(".pdf")) &&
                   !(previewFile instanceof Blob && previewFile.type === "application/pdf") &&
                   !(typeof previewFile === "string" && previewFile.toLowerCase().match(/\.(xls|xlsx|excel)$/i)) &&
                   !(previewFile instanceof Blob && (previewFile.type === "application/vnd.ms-excel" || previewFile.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || previewFile.name?.toLowerCase().match(/\.(xls|xlsx|excel)$/i))) &&
                   !(typeof previewFile === "string" && (previewFile.toLowerCase().endsWith(".csv") || previewFile.toLowerCase().endsWith(".csvx"))) &&
                   !(previewFile instanceof Blob && (previewFile.type === "text/csv" || previewFile.type === "application/csv" || previewFile.name?.toLowerCase().endsWith(".csv") || previewFile.name?.toLowerCase().endsWith(".csvx"))) &&
                   !(previewContent?.type === "csv" || previewContent?.type === "excel") &&
                   !(typeof previewFile === "string" && previewFile.toLowerCase().endsWith(".txt")) &&
                   !(previewFile instanceof Blob && (previewFile.type === "text/plain" || previewFile.name?.toLowerCase().endsWith(".txt"))) && (
                    <Box 
                      style={{ 
                        display: "flex", 
                        gap: "4px", 
                        alignItems: "center",
                        backgroundColor: "#f5f5f5",
                        padding: "4px 8px",
                        borderRadius: "8px",
                      }}
                    >
                      <IconButton
                        onClick={handleZoomOut}
                        disabled={imageZoom <= 50}
                        size="small"
                        style={{ 
                          color: imageZoom <= 50 ? "#ccc" : "#0f2b46",
                          padding: "4px",
                        }}
                        title="Zoom Out"
                      >
                        <ZoomOut fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="body2"
                        style={{
                          minWidth: "50px",
                          textAlign: "center",
                          color: "#0f2b46",
                          fontWeight: 600,
                          fontSize: "14px",
                          padding: "0 8px",
                        }}
                      >
                        {imageZoom}%
                      </Typography>
                      <IconButton
                        onClick={handleZoomIn}
                        disabled={imageZoom >= 300}
                        size="small"
                        style={{ 
                          color: imageZoom >= 300 ? "#ccc" : "#0f2b46",
                          padding: "4px",
                        }}
                        title="Zoom In"
                      >
                        <ZoomIn fontSize="small" />
                      </IconButton>
                      <Button
                        onClick={handleResetZoom}
                        size="small"
                        variant="outlined"
                        style={{ 
                          color: "#0f2b46",
                          borderColor: "#0f2b46",
                          marginLeft: "8px",
                          padding: "2px 12px",
                          fontSize: "12px",
                          textTransform: "none",
                          minWidth: "auto",
                        }}
                        title="Reset Zoom"
                      >
                        Reset
                      </Button>
                      <IconButton
                        onClick={closePreviewModal}
                        size="small"
                        style={{ 
                          color: "#0f2b46", 
                          marginLeft: "8px",
                          padding: "4px",
                        }}
                        title="Close"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  {((typeof previewFile === "string" && previewFile.toLowerCase().endsWith(".pdf")) ||
                    (previewFile instanceof Blob && previewFile.type === "application/pdf") ||
                    (typeof previewFile === "string" && previewFile.toLowerCase().match(/\.(xls|xlsx|excel)$/i)) ||
                    (previewFile instanceof Blob && (previewFile.type === "application/vnd.ms-excel" || previewFile.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || previewFile.name?.toLowerCase().match(/\.(xls|xlsx|excel)$/i))) ||
                    (typeof previewFile === "string" && (previewFile.toLowerCase().endsWith(".csv") || previewFile.toLowerCase().endsWith(".csvx"))) ||
                    (previewFile instanceof Blob && (previewFile.type === "text/csv" || previewFile.type === "application/csv" || previewFile.name?.toLowerCase().endsWith(".csv") || previewFile.name?.toLowerCase().endsWith(".csvx"))) ||
                    (previewContent?.type === "csv" || previewContent?.type === "excel") ||
                    (typeof previewFile === "string" && previewFile.toLowerCase().endsWith(".txt")) ||
                    (previewFile instanceof Blob && (previewFile.type === "text/plain" || previewFile.name?.toLowerCase().endsWith(".txt")))) && (
                    <IconButton
                      onClick={closePreviewModal}
                      size="small"
                      style={{ color: "#0f2b46" }}
                      title="Close"
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </DialogTitle>
                <DialogContent
                  style={{
                    textAlign: "center",
                    padding: "0",
                    overflow: "hidden",
                    backgroundColor: "#f5f5f5",
                    position: "relative",
                    flex: "1 1 auto",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {isLoadingPreview ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                      minHeight={400}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (previewContent?.type === "csv" || previewContent?.type === "excel") ? (
                    previewContent?.error ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "40vh",
                          padding: "40px",
                        }}
                      >
                        <DescriptionIcon
                          sx={{
                            width: "80px",
                            height: "80px",
                            color: previewContent?.type === "csv" ? "#0f2b46" : "#217346",
                            marginBottom: "20px",
                          }}
                        />
                        <Typography variant="h6" sx={{ marginBottom: "10px", fontWeight: 600 }}>
                          {typeof previewFile === "string" ? previewFile : previewFile?.name || `${previewContent?.type === "csv" ? "CSV" : "Excel"} File`}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", textAlign: "center", marginBottom: "10px" }}>
                          Failed to preview {previewContent?.type === "csv" ? "CSV" : "Excel"} file: {previewContent.error}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", textAlign: "center" }}>
                          Please download the file to view its contents.
                        </Typography>
                      </Box>
                    ) : previewContent?.data && previewContent.data.length > 0 ? (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <TableContainer
                          component={Paper}
                          sx={{
                            maxHeight: "calc(90vh - 220px)",
                            overflow: "auto",
                            borderRadius: 0,
                            "& .MuiTable-root": {
                              borderCollapse: "separate",
                              borderSpacing: 0,
                            },
                          }}
                        >
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                {previewContent?.data?.[0]?.map((header, index) => (
                                  <TableCell
                                    key={index}
                                    className="bg-blue-color"
                                    sx={{
                                      textAlign: "left",
                                      color: "white",
                                      fontWeight: "bold",
                                      position: "sticky",
                                      top: 0,
                                      zIndex: 1,
                                      padding: { xs: "8px 12px", sm: "12px 16px" },
                                      backgroundColor: "#2c3e50",
                                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                      "&:not(:last-child)": {
                                        borderRight: "1px solid rgba(224, 224, 224, 0.4)",
                                      },
                                    }}
                                    title={header}
                                  >
                                    {header}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {getPreviewData().map((row, rowIndex) => (
                                <TableRow
                                  key={rowIndex}
                                  sx={{
                                    "&:nth-of-type(even)": {
                                      backgroundColor: "#f9f9f9",
                                    },
                                    "&:hover": {
                                      backgroundColor: "#f0f0f0",
                                    },
                                  }}
                                >
                                  {row.map((cell, cellIndex) => (
                                    <TableCell
                                      key={cellIndex}
                                      sx={{
                                        padding: { xs: "8px 12px", sm: "12px 16px" },
                                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                        borderRight: "1px solid rgba(224, 224, 224, 0.2)",
                                      }}
                                    >
                                      {cell || ""}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={getTotalRows()}
                          page={previewPage}
                          onPageChange={handlePreviewPageChange}
                          rowsPerPage={previewRowsPerPage}
                          onRowsPerPageChange={handlePreviewRowsPerPageChange}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          sx={{
                            borderTop: "1px solid #e0e0e0",
                            backgroundColor: "white",
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "40vh",
                          padding: "40px",
                        }}
                      >
                        <DescriptionIcon
                          sx={{
                            width: "80px",
                            height: "80px",
                            color: previewContent?.type === "csv" ? "#0f2b46" : "#217346",
                            marginBottom: "20px",
                          }}
                        />
                        <Typography variant="h6" sx={{ marginBottom: "10px", fontWeight: 600 }}>
                          {typeof previewFile === "string" ? previewFile : previewFile?.name || `${previewContent?.type === "csv" ? "CSV" : "Excel"} File`}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", textAlign: "center" }}>
                          {previewContent?.type === "csv" ? "CSV" : "Excel"} file is empty or has no data to display.
                        </Typography>
                      </Box>
                    )
                  ) : typeof previewFile === "string" ? (
                    (previewFile.toLowerCase().endsWith(".csv") || previewFile.toLowerCase().endsWith(".csvx")) ? (
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "40vh",
                          padding: "40px",
                        }}
                      >
                        <DescriptionIcon
                          style={{
                            width: "80px",
                            height: "80px",
                            color: "#0f2b46",
                            marginBottom: "20px",
                          }}
                        />
                        <Typography variant="h6" style={{ marginBottom: "10px", fontWeight: 600 }}>
                          {previewFile}
                        </Typography>
                        <Typography variant="body2" style={{ color: "#666", textAlign: "center" }}>
                          CSV files cannot be previewed in the browser. Please download to view.
                        </Typography>
                      </Box>
                    ) : previewFile.toLowerCase().endsWith(".pdf") ? (
                      <iframe
                        src={previewPdfBlobUrl || `${cdnUrl}/v1/cdn/upload/${previewFile}`}
                        style={{
                          width: "100%",
                          height: "50vh",
                          border: "none",
                          borderRadius: "8px",
                        }}
                        title="File Preview"
                      />
                    ) : previewFile.toLowerCase().endsWith(".txt") ? (
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "40vh",
                          padding: "40px",
                        }}
                      >
                        <InsertDriveFileIcon
                          style={{
                            width: "80px",
                            height: "80px",
                            color: "#0f2b46",
                            marginBottom: "20px",
                          }}
                        />
                        <Typography variant="h6" style={{ marginBottom: "10px", fontWeight: 600 }}>
                          {previewFile}
                        </Typography>
                        <Typography variant="body2" style={{ color: "#666", textAlign: "center" }}>
                          Text files cannot be previewed in the browser. Please download to view.
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        style={{
                          display: "flex",
                          justifyContent: imageZoom === 100 ? "center" : "flex-start",
                          alignItems: imageZoom === 100 ? "center" : "flex-start",
                          minHeight: "40vh",
                          padding: "20px",
                          width: "100%",
                          boxSizing: "border-box",
                          overflow: "visible",
                        }}
                      >
                        <img
                          ref={imgRef}
                          src={previewFile ? `${cdnUrl}/v1/cdn/upload/${previewFile}` : ""}
                          alt="Preview"
                          onLoad={updateImageSize}
                          style={{
                            width: "auto",
                            maxWidth: imageZoom === 100 ? "100%" : "none",
                            height: "auto",
                            borderRadius: "8px",
                            boxShadow: "var(--shadow-md)",
                            objectFit: "contain",
                            transition: "width 0.3s ease",
                            display: "block",
                          }}
                          draggable={false}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/assets/images/no-image.png";
                          }}
                        />
                      </Box>
                    )
                  ) : previewFile instanceof Blob || previewFile instanceof File ? (
                    previewFile.type === "application/pdf" || previewFile.name?.toLowerCase().endsWith(".pdf") ? (
                      <iframe
                        src={URL.createObjectURL(previewFile)}
                        style={{
                          width: "100%",
                          height: "50vh",
                          border: "none",
                          borderRadius: "8px",
                        }}
                        title="File Preview"
                      />
                    ) : previewFile.type === "text/plain" || previewFile.name?.toLowerCase().endsWith(".txt") ? (
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: "40vh",
                          padding: "40px",
                        }}
                      >
                        <InsertDriveFileIcon
                          style={{
                            width: "80px",
                            height: "80px",
                            color: "#0f2b46",
                            marginBottom: "20px",
                          }}
                        />
                        <Typography variant="h6" style={{ marginBottom: "10px", fontWeight: 600 }}>
                          {previewFile.name || "Uploaded File"}
                        </Typography>
                        <Typography variant="body2" style={{ color: "#666", textAlign: "center" }}>
                          Text files cannot be previewed in the browser. Please download to view.
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        style={{
                          display: "flex",
                          justifyContent: imageZoom === 100 ? "center" : "flex-start",
                          alignItems: imageZoom === 100 ? "center" : "flex-start",
                          minHeight: "40vh",
                          padding: "20px",
                          width: "100%",
                          boxSizing: "border-box",
                          overflow: "visible",
                        }}
                      >
                        <img
                          ref={imgRef}
                          src={URL.createObjectURL(previewFile)}
                          alt="Preview"
                          onLoad={updateImageSize}
                          style={{
                            width: "auto",
                            maxWidth: imageZoom === 100 ? "100%" : "none",
                            height: "auto",
                            borderRadius: "8px",
                            boxShadow: "var(--shadow-md)",
                            objectFit: "contain",
                            transition: "width 0.3s ease",
                            display: "block",
                          }}
                          draggable={false}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/assets/images/no-image.png";
                          }}
                        />
                      </Box>
                    )
                  ) : null
                  }
                </DialogContent>
                <DialogActions style={{ padding: "8px 24px" }}>
                  <WhiteButton
                    onClick={closePreviewModal}
                    label="Close"
                  />
                </DialogActions>
              </Dialog>

              <Col
                className="col-4 d-flex flex-column justify-content-around align-items-end"
                style={{ minHeight: "120px" }}
                xl={4}
              >
                
                <DeleteForeverRoundedIcon
                  style={{ color: "red", cursor: "pointer" }}
                  onClick={handleRemove}
                />
              </Col>
            </Row>
          )}
        </span>
      </Grid>
      <FileModal open={modalOpen} setOpen={setModalOpen} file={file} />
    </Grid>
  );
};

const SingleFileUpload = ({ file, setFile }) => {
  const effectiveAllowedExtensions = allowedExtensions;
  const multiDropSx = useMultiFileDropZoneSx();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (!isValidFileType(droppedFile)) {
        showToast.warning("File type not allowed");
      } else if (!isValidFileSize(droppedFile)) {
        const limitMB = getFileSizeLimitMB(droppedFile);
        showToast.warning(`${droppedFile.name} exceeds the maximum size limit of ${limitMB}MB`);
      } else {
        setFile(droppedFile);
      }
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (!isValidFileType(selectedFile)) {
        showToast.warning("File type not allowed");
      } else if (!isValidFileSize(selectedFile)) {
        const limitMB = getFileSizeLimitMB(selectedFile);
        showToast.warning(`${selectedFile.name} exceeds the maximum size limit of ${limitMB}MB`);
      } else {
        setFile(selectedFile);
      }
    }
  };

  return (
    <Grid className="file-upload-container productDragFole">
      <Grid
        className="file-upload file-upload--s7"
        sx={multiDropSx}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="Attachment"
          name="Attachment"
          className="file-input"
          accept={effectiveAllowedExtensions.join(",")}
          onChange={handleChange}
        />
        <label className="upload-files-label addQUoteFileSelect">
          Drag your file here or{" "}
          <span
            onClick={() => document.getElementById("Attachment").click()}
            className="select-file-link"
          >
            Select a File
          </span>
        </label>
      </Grid>
    </Grid>
  );
};

export { Files, File, SingleFileUpload };
